import { useCallback, useEffect, useRef, useState } from 'react'
import { useDeviceType } from '@/utils'
import { useAppDispatch, useAppSelector } from '@/redux/core'
import { selectCurrencyCode, setCurrency } from '@/redux/currency'
import {
  clearConversations,
  closeConversation,
  openConversation,
  removeConversation,
  selectActiveConversationId,
  selectConversations,
} from '@/redux/conversations'
import { railTransitionMs } from './SidebarLayout.data'

export function useSidebarLayoutController() {
  const dispatch = useAppDispatch()
  const { isMobile } = useDeviceType()

  // Desktop: whether the rail is expanded into the full panel. Mobile: whether
  // the drawer is open. Either way it starts closed and lives for the session.
  const [isExpanded, setIsExpanded] = useState(false)

  // Tracked apart from the width so labels can be swapped at the edges of the
  // change rather than during it — see `onToggle`.
  const [areLabelsVisible, setAreLabelsVisible] = useState(false)
  const labelTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const currencyCode = useAppSelector(selectCurrencyCode)
  const conversations = useAppSelector(selectConversations)
  const activeConversationId = useAppSelector(selectActiveConversationId)

  const clearLabelTimer = useCallback(() => clearTimeout(labelTimerRef.current), [])

  const onClose = useCallback(() => {
    clearLabelTimer()
    setAreLabelsVisible(false)
    setIsExpanded(false)
  }, [clearLabelTimer])

  /**
   * Labels leave before a collapse starts and arrive after an expand finishes, so
   * no text is ever laid out at a width it doesn't fit. Timed off the transition
   * length rather than `transitionend`, which never fires if the width doesn't
   * actually change and would leave the panel open with nothing in it.
   */
  const onToggle = useCallback(() => {
    if (isExpanded) {
      onClose()
      return
    }

    clearLabelTimer()
    setIsExpanded(true)
    labelTimerRef.current = setTimeout(() => setAreLabelsVisible(true), railTransitionMs)
  }, [isExpanded, onClose, clearLabelTimer])

  useEffect(() => clearLabelTimer, [clearLabelTimer])

  const closeOnMobile = useCallback(() => {
    if (isMobile) onClose()
  }, [isMobile, onClose])

  const onCurrencyChange = useCallback((code: string) => dispatch(setCurrency(code)), [dispatch])

  const onRemoveConversation = useCallback(
    (id: string) => dispatch(removeConversation(id)),
    [dispatch],
  )

  const onClearConversations = useCallback(() => dispatch(clearConversations()), [dispatch])

  // Switching conversations is a store change — the thread follows the active
  // conversation, so nothing is appended to the chat that was on screen.
  const onOpenConversation = useCallback(
    (id: string) => {
      dispatch(openConversation(id))
      closeOnMobile()
    },
    [dispatch, closeOnMobile],
  )

  const onNewSearch = useCallback(() => {
    dispatch(closeConversation())
    closeOnMobile()
  }, [dispatch, closeOnMobile])

  return {
    isExpanded,
    asideVariant: isMobile ? ('mobile' as const) : ('desktop' as const),
    // Only the desktop panel morphs between a rail and a panel, so only it needs
    // the fixed icon column. The drawer keeps the original centred buttons.
    isRail: !isMobile,
    // The mobile drawer slides in at full width, so its labels are never cropped
    // and can show as soon as it opens.
    showLabels: isMobile ? isExpanded : areLabelsVisible,
    // Mobile only: on desktop the rail carries the brand and the toggle, so a bar
    // above the content would only duplicate them — and unmounting one is what
    // used to shift the whole page.
    showTopBar: isMobile,
    currencyCode,
    conversations,
    activeConversationId,
    onToggle,
    onClose,
    onCurrencyChange,
    onOpenConversation,
    onRemoveConversation,
    onClearConversations,
    onNewSearch,
  }
}
